import { format } from "date-fns";
import type { MovementActivity } from "../../lib/api";

export const inclusiveDaysBetween = (
  dateFrom: string,
  dateTo: string,
): number | null => {
  if (!dateFrom || !dateTo) return null;
  const fromMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateFrom);
  const toMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateTo);
  if (!fromMatch || !toMatch) return null;
  const from = Date.UTC(
    Number(fromMatch[1]),
    Number(fromMatch[2]) - 1,
    Number(fromMatch[3]),
  );
  const to = Date.UTC(
    Number(toMatch[1]),
    Number(toMatch[2]) - 1,
    Number(toMatch[3]),
  );
  if (to < from) return null;
  return Math.floor((to - from) / (24 * 60 * 60 * 1000)) + 1;
};

const toDateInputValue = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  return new Date(trimmed).toISOString().slice(0, 10);
};

const addDaysToDateInput = (dateStr: string, days: number): string => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const getMovementDaysHired = (movement: MovementActivity): number =>
  Number(movement.daysHired ?? (movement as any).days_hired ?? 0);

/** Extension start: hire start if no days hired yet, else day after current hire end. */
export const getDefaultExtensionDateFrom = (
  movement: MovementActivity,
): string => {
  const hireStart =
    toDateInputValue(movement.plannedDate) ||
    toDateInputValue(movement.createdAt);
  if (!hireStart) return "";
  const daysHired = getMovementDaysHired(movement);
  if (!Number.isFinite(daysHired) || daysHired <= 0) return hireStart;
  return addDaysToDateInput(hireStart, daysHired);
};

export const canExtendMovementDaysHired = (
  movement: MovementActivity,
): boolean => {
  const status = String(movement.status ?? "").toUpperCase();
  if (status === "CANCELLED") return false;
  const subType = String(movement.activitySubType ?? "").toUpperCase();
  const activityType = String(movement.activityType ?? "").toUpperCase();
  return (
    subType === "DELIVER" ||
    (subType === "TRANSFER" && activityType === "OUTFLOW")
  );
};

export const formatExtendableMovementLabel = (
  movement: MovementActivity,
): string => {
  const ref = movement.reference?.trim() || movement.id.slice(0, 8);
  const date = movement.plannedDate || movement.createdAt;
  const dateLabel = date ? format(new Date(date), "MMM dd, yyyy") : "—";
  const daysHired = getMovementDaysHired(movement);
  const daysLabel = Number.isFinite(daysHired) ? ` · ${daysHired} days` : "";
  return `${ref} · ${movement.activitySubType} · ${dateLabel}${daysLabel}`;
};

export const formatExtensionDateFrom = (dateFrom: string): string =>
  format(new Date(`${dateFrom}T00:00:00`), "MMM dd, yyyy");

export const isExtensionPeriodInvalid = (
  movement: MovementActivity | undefined,
  dateTo: string,
): boolean => {
  if (!movement) return true;
  const dateFrom = getDefaultExtensionDateFrom(movement);
  const calculatedDays = inclusiveDaysBetween(dateFrom, dateTo);
  return !dateFrom || calculatedDays == null || calculatedDays < 1;
};

/** Earliest allowed "Date To": the earliest extension start among the selection. */
export const getExtensionMinDate = (
  selectedIds: string[],
  movements: MovementActivity[],
): string | undefined =>
  selectedIds.length > 0
    ? selectedIds
        .map((id) =>
          getDefaultExtensionDateFrom(
            movements.find((movement) => movement.id === id)!,
          ),
        )
        .filter(Boolean)
        .sort()[0]
    : undefined;
